import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';
import DialogShell from '../ui/DialogShell';

interface RequestAccessModalProps {
    onClose: () => void;
}

function RequestAccessModal({ onClose }: RequestAccessModalProps) {
    const titleId = React.useId();
    const initialFocusRef = useRef<HTMLInputElement>(null);
    const [email, setEmail] = useState('');
    const [companyWebpage, setCompanyWebpage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    const handleRequestAccess = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        
        try {
            // Create mailto link with request details
            const subject = encodeURIComponent('BARK Access Request');
            const body = encodeURIComponent(
                `New access request:\n\n` +
                `Email: ${email}\n` +
                `Company Webpage: ${companyWebpage}\n\n` +
                `Please review and create an account for this user.`
            );
            
            // Open default email client
            window.location.href = `mailto:info@barktechnologies.se?subject=${subject}&body=${body}`;
            
            setIsLoading(false);
            setMessage("Your default email client has been opened. Please send the email to complete your request.");
            setIsSuccess(true);
            setTimeout(() => { onClose(); }, 4000);
        } catch (error) {
            setIsLoading(false);
            setMessage("Failed to open email client. Please email info@barktechnologies.se directly.");
        }
    };

    return (
        <DialogShell
            open
            onClose={onClose}
            labelledBy={titleId}
            backdropPolicy={!isSuccess && (email !== '' || companyWebpage !== '') ? 'static' : 'dismiss'}
            initialFocusRef={initialFocusRef}
            overlayClassName="flex items-center justify-center p-4"
            panelClassName="w-full max-w-md relative overflow-hidden"
        >
                <button type="button" onClick={onClose} aria-label="Close request access" className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:bg-gray-100">
                    <X size={24} strokeWidth={1.5} aria-hidden="true" />
                </button>
                <div className="p-8">
                    <h2 id={titleId} className="text-center text-xl font-semibold tracking-tight text-gray-900">Request access to BARK</h2>
                    {message && <p className={`text-center text-sm mt-4 ${isSuccess ? 'text-success' : 'text-danger'}`}>{message}</p>}
                    {!isSuccess && (
                        <form onSubmit={handleRequestAccess} className="mt-8 space-y-6">
                            <div className="rounded-md -space-y-px">
                                <input 
                                    ref={initialFocusRef}
                                    value={email} 
                                    onChange={e => setEmail(e.target.value)} 
                                    type="email" 
                                    name="email"
                                    autoComplete="email"
                                    aria-label="Work email address"
                                    required 
                                    className="relative block w-full appearance-none rounded-t-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm" 
                                    placeholder="Work email address"
                                />
                                <input 
                                    value={companyWebpage} 
                                    onChange={e => setCompanyWebpage(e.target.value)} 
                                    type="url" 
                                    name="company-webpage"
                                    autoComplete="url"
                                    aria-label="Company webpage"
                                    required 
                                    className="relative block w-full appearance-none rounded-b-md border border-gray-300 px-3 py-3 text-gray-900 placeholder-gray-500 focus:z-10 focus:border-accent focus:outline-none focus:ring-accent sm:text-sm" 
                                    placeholder="Company webpage (e.g., https://bank.com)"
                                />
                            </div>
                            <button 
                                type="submit" 
                                disabled={isLoading} 
                                className="group relative flex w-full justify-center rounded-md border border-transparent bg-accent py-3 px-4 text-sm font-semibold text-white hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:bg-accent"
                            >
                                {isLoading ? 'Submitting...' : 'Request access'}
                            </button>
                        </form>
                    )}
                </div>
        </DialogShell>
    );
}

export default RequestAccessModal;
